/**
 * Cache invalidation utilities for React Query
 * Provides centralized cache invalidation strategies for related data
 */

import { QueryClient } from '@tanstack/react-query';

/**
 * Invalidate all queries related to a specific group/cohort
 * Use after operations that modify group data, participants, or goals
 */
export const invalidateGroupQueries = (queryClient: QueryClient, groupId: number) => {
  queryClient.invalidateQueries({ queryKey: ['GroupViewCreator', groupId] });
  queryClient.invalidateQueries({ queryKey: ['ActiveParticipantsViewCreator', groupId] });
  queryClient.invalidateQueries({ queryKey: ['LeaderboardViewCreator', groupId] });
  queryClient.invalidateQueries({ queryKey: ['PledgePotViewCreator', groupId] });
  queryClient.invalidateQueries({ queryKey: ['CohortPledgeViewCreator', groupId] });
  queryClient.invalidateQueries({ queryKey: ['AdminEngagementReportViewCreator', groupId] });
  queryClient.invalidateQueries({ queryKey: ['AdminPrizeDrawingsViewCreator', groupId] });
  queryClient.invalidateQueries({ queryKey: ['nextSurveySectionViewCreator', groupId] });
};

/**
 * Invalidate the Questionnaires admin's questionnaire list and version history
 * Use after publishing a questionnaire
 */
export const invalidateQuestionnaireAdminQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['QuestionnaireAdminViewCreator', 'summaries'] });
  queryClient.invalidateQueries({ queryKey: ['QuestionnaireAdminViewCreator', 'versions'] });
};

/**
 * Invalidate the Questionnaires admin's schedules and timepoints
 * Use after saving a schedule or adding a timepoint
 */
export const invalidateQuestionnaireScheduleQueries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: ['QuestionnaireAdminViewCreator', 'schedules'] });
  queryClient.invalidateQueries({ queryKey: ['QuestionnaireAdminViewCreator', 'timepoints'] });
};

/**
 * Invalidate all queries related to a specific cohort user
 * Use after operations that modify user goals, activities, or profile
 */
export const invalidateCohortUserQueries = (queryClient: QueryClient, cohortUserId: number) => {
  queryClient.invalidateQueries({ queryKey: ['activityHistoryViewCreator', cohortUserId] });
  queryClient.invalidateQueries({ queryKey: ['GoalCardViewCreator', cohortUserId] });
};

/**
 * Invalidate all queries related to user profile
 * Use after profile updates or preference changes
 */
export const invalidateUserQueries = (queryClient: QueryClient, userId: number) => {
  queryClient.invalidateQueries({ queryKey: ['UserProfileViewCreator', userId] });
  queryClient.invalidateQueries({ queryKey: ['CommunicationPreferencesViewCreator'] });
};

/**
 * Invalidate accountability partner queries
 * Use after pairing changes
 */
export const invalidateAccountabilityPartnerQueries = (queryClient: QueryClient, groupId: number) => {
  queryClient.invalidateQueries({ queryKey: ['CohortAccountabilityPartnersViewCreator', groupId] });
};

/**
 * Prefetch group data for predictable navigation
 * Call when user is likely to navigate to a group page
 */
export const prefetchGroupData = async (queryClient: QueryClient, groupId: number) => {
  // Prefetch is handled by individual view creators with their own prefetch hooks
  // This is a placeholder for future implementation if needed
};
